import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { 
    faHistory, faSearch, faCrown, faArrowTrendUp, 
    faUsers, faCoins, faCalendarDay, faWallet, faShoppingCart, 
    faTools, faPlus, faEdit, faCreditCard, faMoneyBillWave, faGem, faTrash
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminWallet = () => {
    const { t } = useTranslation();
    const [viewMode, setViewMode] = useState('day'); 
    const [filterValue, setFilterValue] = useState(new Date().toISOString().split('T')[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [transactions, setTransactions] = useState([]);
    const [tableFilter, setTableFilter] = useState('all');
    const [loading, setLoading] = useState(false);

    const [topStats, setTopStats] = useState({ day: 0, month: 0, year: 0 });
    const [miniStats, setMiniStats] = useState({ 
        income: 0, online: 0, workers: 0, orders: 0, admin: 0 
    });

    const [transactionEntry, setTransactionEntry] = useState({
        date: new Date().toISOString().split('T')[0],
        income: '',
        payment_method: 'cash',
        worker_salary: '',
        order_expense: ''
    });

    const [adminExpense, setAdminExpense] = useState({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        description: ''
    });

    const getHeaders = useCallback(() => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }), []);

    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = getHeaders();
            const today = new Date().toISOString().split('T')[0];
            const thisMonth = today.slice(0, 7);
            const thisYear = today.slice(0, 4);

            const [dRes, mRes, yRes] = await Promise.all([
                fetch(`${API_BASE}/api/wallet/stats?type=day&value=${today}`, { headers }).then(r => r.json()),
                fetch(`${API_BASE}/api/wallet/stats?type=month&value=${thisMonth}`, { headers }).then(r => r.json()),
                fetch(`${API_BASE}/api/wallet/stats?type=year&value=${thisYear}`, { headers }).then(r => r.json())
            ]);

            setTopStats({
                day: dRes.total_profit || 0,
                month: mRes.total_profit || 0,
                year: yRes.total_profit || 0
            });

            const currentRes = await fetch(`${API_BASE}/api/wallet/stats?type=${viewMode}&value=${filterValue}`, { headers }).then(r => r.json());
            setMiniStats({
                income: currentRes.total_income || 0,
                online: currentRes.total_online || 0,
                workers: currentRes.total_worker_salary || 0,
                orders: currentRes.total_order_expense || 0,
                admin: currentRes.total_admin_expense || 0
            });

            const resTable = await fetch(`${API_BASE}/api/wallet/transactions?type=${viewMode}&value=${filterValue}`, { headers });
            const tableData = await resTable.json();
            setTransactions(Array.isArray(tableData) ? tableData : []);

        } catch (err) { 
            console.error("Error loading wallet data:", err); 
        } finally {
            setLoading(false);
        }
    }, [viewMode, filterValue, getHeaders]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleTransactionSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE}/api/wallet/add-transaction`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(transactionEntry)
            });
            if (res.ok) {
                setTransactionEntry({ date: new Date().toISOString().split('T')[0], income: '', payment_method: 'cash', worker_salary: '', order_expense: '' });
                loadData();
            }
        } catch (err) { console.error(err); }
    };

    const handleAdminSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE}/api/wallet/add-admin-expense`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(adminExpense)
            });
            if (res.ok) {
                setAdminExpense({ date: new Date().toISOString().split('T')[0], amount: '', description: '' });
                loadData();
            }
        } catch (err) { console.error(err); }
    };

    const handleUpdate = async (t_data) => {
        const nInc = prompt(t('wallet.prompt_income'), t_data.income);
        const nMeth = prompt(t('wallet.prompt_method'), t_data.payment_method || 'cash');
        const nSal = prompt(t('wallet.prompt_salary'), t_data.worker_salary);
        const nOrd = prompt(t('wallet.prompt_expense'), t_data.order_expense);
        const nAdm = prompt(t('wallet.prompt_other_expense'), t_data.admin_expense);
        const nDsc = prompt(t('wallet.prompt_desc'), t_data.admin_desc);

        if (nInc === null) return; 

        try {
            await fetch(`${API_BASE}/api/wallet/update-transaction/${t_data.id}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ income: nInc, payment_method: nMeth, worker_salary: nSal, order_expense: nOrd, admin_expense: nAdm, admin_desc: nDsc })
            });
            loadData();
        } catch (err) { console.error(err); }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('wallet.confirm_delete'))) {
            try {
                const res = await fetch(`${API_BASE}/api/wallet/delete-transaction/${id}`, {
                    method: 'DELETE',
                    headers: getHeaders()
                });
                if (res.ok) loadData();
            } catch (err) { console.error(err); }
        }
    };

    const filteredTransactions = transactions
        .filter(t_item => (t_item.admin_desc || '').toLowerCase().includes(searchTerm.toLowerCase()))
        .filter(t_item => {
            if (tableFilter === 'online') return t_item.payment_method === 'online';
            if (tableFilter === 'cash') return t_item.payment_method === 'cash';
            return true;
        });

    const totalIncome = Number(miniStats.income) + Number(miniStats.online);
    const totalExpenses = Number(miniStats.orders) + Number(miniStats.admin) + Number(miniStats.workers);
    const netProfit = totalIncome - totalExpenses;

    return (
        <div className="master-container" style={{ opacity: loading ? 0.7 : 1, transition: '0.3s' }}>
            <style>{masterStyles}</style>
            <div className="main-content">
                <aside className="sidebar-glass">
                    <div className="brand-zone">
                        <div className="icon-main"><FontAwesomeIcon icon={faCrown} /></div>
                        <h2>Admin <span>{t('wallet.title')}</span></h2>
                    </div>

                    <div className="control-card">
                        <label className="label-mini">{t('wallet.view_mode')}</label>
                        <div className="pill-selector">
                            <button className={viewMode === 'day' ? 'active' : ''} onClick={() => { setViewMode('day'); setFilterValue(new Date().toISOString().split('T')[0]); }}>{t('wallet.day')}</button>
                            <button className={viewMode === 'month' ? 'active' : ''} onClick={() => { setViewMode('month'); setFilterValue(new Date().toISOString().slice(0, 7)); }}>{t('wallet.month')}</button>
                            <button className={viewMode === 'year' ? 'active' : ''} onClick={() => { setViewMode('year'); setFilterValue(new Date().getFullYear().toString()); }}>{t('wallet.year')}</button>
                        </div>
                        <input className="neo-input" type={viewMode === 'month' ? 'month' : viewMode === 'year' ? 'number' : 'date'} value={filterValue} onChange={e => setFilterValue(e.target.value)} />
                    </div>

                    <div className="control-card border-blue">
                        <label className="label-mini txt-blue"><FontAwesomeIcon icon={faPlus} /> {t('wallet.new_transaction')}</label>
                        <form onSubmit={handleTransactionSubmit}>
                            <input className="neo-input" type="date" value={transactionEntry.date} onChange={e => setTransactionEntry({...transactionEntry, date: e.target.value})} />
                            <div className="input-group-row">
                                <input className="neo-input" type="number" placeholder={t('wallet.income')} value={transactionEntry.income} onChange={e => setTransactionEntry({...transactionEntry, income: e.target.value})} />
                                <select className="neo-input" value={transactionEntry.payment_method} onChange={e => setTransactionEntry({...transactionEntry, payment_method: e.target.value})}>
                                    <option value="cash">{t('wallet.cash')}</option>
                                    <option value="online">{t('wallet.online')}</option>
                                </select>
                            </div>
                            <input className="neo-input" type="number" placeholder={t('wallet.salary_neg')} value={transactionEntry.worker_salary} onChange={e => setTransactionEntry({...transactionEntry, worker_salary: e.target.value})} />
                            <input className="neo-input" type="number" placeholder={t('wallet.expense_neg')} value={transactionEntry.order_expense} onChange={e => setTransactionEntry({...transactionEntry, order_expense: e.target.value})} />
                            <button type="submit" className="glow-btn btn-blue">{t('wallet.btn_register')}</button>
                        </form>
                    </div>

                    <div className="control-card border-red">
                        <label className="label-mini txt-red"><FontAwesomeIcon icon={faTools} /> {t('wallet.admin_expense')}</label>
                        <form onSubmit={handleAdminSubmit}>
                            <input className="neo-input" type="date" value={adminExpense.date} onChange={e => setAdminExpense({...adminExpense, date: e.target.value})} />
                            <input className="neo-input" type="number" placeholder={t('wallet.amount_neg')} value={adminExpense.amount} onChange={e => setAdminExpense({...adminExpense, amount: e.target.value})} />
                            <textarea className="neo-input" placeholder={t('wallet.purpose_placeholder')} value={adminExpense.description} onChange={e => setAdminExpense({...adminExpense, description: e.target.value})} />
                            <button type="submit" className="glow-btn btn-red">{t('wallet.btn_register_expense')}</button>
                        </form>
                    </div>
                </aside>

                <section className="dashboard-area">
                    <div className="profit-highlights">
                        <div className="h-card daily"><span>{t('wallet.today')}</span><h3>{Number(topStats.day).toLocaleString()} ֏</h3><FontAwesomeIcon icon={faCalendarDay} className="card-icon" /></div>
                        <div className="h-card monthly"><span>{t('wallet.this_month')}</span><h3>{Number(topStats.month).toLocaleString()} ֏</h3><FontAwesomeIcon icon={faArrowTrendUp} className="card-icon" /></div>
                        <div className="h-card yearly"><span>{t('wallet.this_year')}</span><h3>{Number(topStats.year).toLocaleString()} ֏</h3><FontAwesomeIcon icon={faCoins} className="card-icon" /></div>
                    </div>

                    <div className="stats-row-4">
                        <div className="m-card border-green"><FontAwesomeIcon icon={faMoneyBillWave} className="txt-green" /><div><small>{t('wallet.cash_income')}</small><strong>{Number(miniStats.income).toLocaleString()} ֏</strong></div></div>
                        <div className="m-card border-purple"><FontAwesomeIcon icon={faCreditCard} className="txt-purple" /><div><small>{t('wallet.online_income')}</small><strong>{Number(miniStats.online).toLocaleString()} ֏</strong></div></div>
                        <div className="m-card border-orange"><FontAwesomeIcon icon={faShoppingCart} className="txt-orange" /><div><small>{t('wallet.order_expense_label')}</small><strong>{Number(miniStats.orders).toLocaleString()} ֏</strong></div></div>
                        <div className="m-card border-red"><FontAwesomeIcon icon={faTools} className="txt-red" /><div><small>{t('wallet.other_expenses_label')}</small><strong>{Number(miniStats.admin).toLocaleString()} ֏</strong></div></div>
                    </div>

                    <div className="stats-row-4">
                        <div className="m-card itog-card bg-green-soft"><FontAwesomeIcon icon={faPlus} className="txt-green" /><div><small>{t('wallet.total_income_label')}</small><strong>{totalIncome.toLocaleString()} ֏</strong></div></div>
                        <div className="m-card itog-card bg-blue-soft"><FontAwesomeIcon icon={faUsers} className="txt-blue" /><div><small>{t('wallet.salaries_label')}</small><strong>{Number(miniStats.workers).toLocaleString()} ֏</strong></div></div>
                        <div className="m-card itog-card bg-red-soft"><FontAwesomeIcon icon={faHistory} className="txt-red" /><div><small>{t('wallet.total_expenses_label')}</small><strong>{totalExpenses.toLocaleString()} ֏</strong></div></div>
                        <div className="m-card itog-card bg-gold-soft"><FontAwesomeIcon icon={faGem} className="txt-gold" /><div><small>{t('wallet.net_profit_label')}</small><strong>{netProfit.toLocaleString()} ֏</strong></div></div>
                    </div>

                    <div className="table-zone-glass">
                        <div className="table-header-flex">
                            <div className="search-mini">
                                <FontAwesomeIcon icon={faSearch} />
                                <input placeholder={t('wallet.search_placeholder')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                            <div className="table-filters">
                                <button className={tableFilter === 'all' ? 'active' : ''} onClick={() => setTableFilter('all')}>{t('wallet.all')}</button>
                                <button className={tableFilter === 'cash' ? 'active' : ''} onClick={() => setTableFilter('cash')}>{t('wallet.cash')}</button>
                                <button className={tableFilter === 'online' ? 'active' : ''} onClick={() => setTableFilter('online')}>{t('wallet.online')}</button>
                            </div>
                        </div>

                        <table className="neo-table">
                            <thead>
                                <tr>
                                    <th>{t('wallet.th_date')}</th>
                                    <th>{t('wallet.th_income')}</th>
                                    <th>{t('wallet.th_salary')}</th>
                                    <th>{t('wallet.th_expense')}</th>
                                    <th>{t('wallet.th_other')}</th>
                                    <th style={{color: '#fbbf24', fontWeight: 'bold'}}>{t('wallet.th_profit')}</th>
                                    <th>{t('wallet.th_purpose')}</th>
                                    <th>{t('wallet.th_tools')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTransactions.map(t_row => {
                                    const rowProfit = Number(t_row.income || 0) - Number(t_row.worker_salary || 0) - Number(t_row.order_expense || 0) - Number(t_row.admin_expense || 0);
                                    return (
                                        <tr key={t_row.id} className="neo-row">
                                            <td data-label={t('wallet.th_date')}>{formatDateDisplay(t_row.date)}</td>
                                            <td data-label={t('wallet.th_income')} className="txt-green">
                                                <strong>{Number(t_row.income || 0).toLocaleString()} ֏</strong>
                                                <small style={{display: 'block', fontSize: '10px', opacity: 0.6}}>
                                                    {t_row.payment_method === 'online' ? t('wallet.online').toUpperCase() : t('wallet.cash').toUpperCase()}
                                                </small>
                                            </td>
                                            <td data-label={t('wallet.th_salary')} className="txt-purple">{Number(t_row.worker_salary || 0).toLocaleString()} ֏</td>
                                            <td data-label={t('wallet.th_expense')} className="txt-orange">{Number(t_row.order_expense || 0).toLocaleString()} ֏</td>
                                            <td data-label={t('wallet.th_other')} className="txt-red">{Number(t_row.admin_expense || 0).toLocaleString()} ֏</td>
                                            <td data-label={t('wallet.th_profit')} style={{color: '#fbbf24', fontWeight: 'bold'}}>{rowProfit.toLocaleString()} ֏</td>
                                            <td data-label={t('wallet.th_purpose')} style={{opacity: 0.7, fontSize: '11px'}}>{t_row.admin_desc || '—'}</td>
                                            <td data-label={t('wallet.th_tools')}>
                                                <div className="row-actions">
                                                    <button className="edit-mini-btn" onClick={() => handleUpdate(t_row)}>
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </button>
                                                    <button className="delete-mini-btn" onClick={() => handleDelete(t_row.id)}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    );
};

const masterStyles = `
    .master-container { background: #070912; min-height: 100vh; padding: 20px; color: #f8fafc; font-family: sans-serif; box-sizing: border-box; }
    .main-content { display: grid; grid-template-columns: minmax(240px, 280px) 1fr; gap: 20px; max-width: 1400px; margin: 0 auto; }
    .dashboard-area { display: flex; flex-direction: column; gap: 15px; min-width: 0; }
    .sidebar-glass { background: #111422; border-radius: 20px; padding: 15px; border: 1px solid #1e293b; height: fit-content; position: sticky; top: 20px; }
    .brand-zone { display: flex; align-items: center; gap: 10px; padding: 10px; }
    .icon-main { background: #fbbf24; color: #000; padding: 8px; border-radius: 10px; }
    .brand-zone h2 { font-size: 18px; margin: 0; }
    .brand-zone h2 span { color: #fbbf24; }
    .control-card { background: #1a1f2e; padding: 12px; border-radius: 15px; border: 1px solid #2d3748; margin-bottom: 10px; }
    .label-mini { font-size: 10px; font-weight: bold; color: #64748b; margin-bottom: 8px; display: block; }
    .neo-input { width: 100%; background: #070912; border: 1px solid #1e293b; padding: 10px; border-radius: 8px; color: #fff; margin-bottom: 8px; outline: none; font-size: 13px; box-sizing: border-box;}
    .pill-selector { display: flex; gap: 2px; background: #070912; padding: 2px; border-radius: 8px; margin-bottom: 8px; }
    .pill-selector button { flex: 1; border: none; background: transparent; color: #64748b; padding: 6px; font-size: 11px; cursor: pointer; border-radius: 6px; }
    .pill-selector button.active { background: #1e293b; color: #fff; }
    .glow-btn { width: 100%; border: none; padding: 10px; border-radius: 8px; font-weight: bold; cursor: pointer; transition: 0.3s; }
    .btn-blue { background: #38bdf8; color: #000; }
    .btn-red { background: #f43f5e; color: #fff; }
    .profit-highlights { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
    .stats-row-4 { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
    .h-card { padding: 20px; border-radius: 20px; position: relative; border: 1px solid #1e293b; }
    .h-card h3 { font-size: 24px; margin: 5px 0 0 0; }
    .card-icon { position: absolute; right: 20px; top: 20px; font-size: 30px; opacity: 0.2; }
    .daily { background: #1e293b; color: #38bdf8; }
    .monthly { background: #1e1b4b; color: #a855f7; }
    .yearly { background: #06201b; color: #10b981; }
    .m-card { background: #111422; padding: 15px; border-radius: 12px; border: 1px solid #1e293b; display: flex; align-items: center; gap: 12px; }
    .m-card strong { display: block; font-size: 16px; }
    .m-card small { font-size: 10px; color: #94a3b8; text-transform: uppercase; }
    .itog-card { background: #1a1f2e; border: 1px dashed #334155; }
    .bg-green-soft { border-left: 4px solid #10b981; }
    .bg-blue-soft { border-left: 4px solid #38bdf8; }
    .bg-red-soft { border-left: 4px solid #f43f5e; }
    .bg-gold-soft { border-left: 4px solid #fbbf24; }

    /* TABLE BASE */
    .table-zone-glass { background: #111422; border-radius: 20px; padding: 20px; border: 1px solid #1e293b; overflow-x: auto; }
    .neo-table { width: 100%; border-collapse: collapse; }
    .neo-table th { text-align: left; padding: 12px; color: #64748b; font-size: 11px; border-bottom: 1px solid #1e293b; text-transform: uppercase; }
    .neo-row td { padding: 15px 12px; border-bottom: 1px solid #1a1f2e; font-size: 13px; }
    .row-actions { display: flex; gap: 5px; }

    .edit-mini-btn { background: transparent; border: 1px solid #38bdf8; color: #38bdf8; padding: 6px; border-radius: 8px; cursor: pointer; transition: 0.2s; }
    .edit-mini-btn:hover { background: #38bdf8; color: #000; }
    .delete-mini-btn { background: transparent; border: 1px solid #f43f5e; color: #f43f5e; padding: 6px; border-radius: 8px; cursor: pointer; transition: 0.2s; }
    .delete-mini-btn:hover { background: #f43f5e; color: #fff; }

    .txt-green { color: #10b981; } .txt-purple { color: #a855f7; } .txt-orange { color: #fb923c; } .txt-red { color: #f43f5e; } .txt-blue { color: #38bdf8; } .txt-gold { color: #fbbf24; }
    .input-group-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .table-header-flex { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; flex-wrap: wrap; gap: 10px; }
    .search-mini { background: #070912; border: 1px solid #1e293b; padding: 8px 12px; border-radius: 10px; display: flex; align-items: center; gap: 8px; }
    .search-mini input { background: transparent; border: none; color: #fff; outline: none; font-size: 13px; width: 150px; }
    .table-filters { display: flex; gap: 5px; }
    .table-filters button { background: #1a1f2e; border: 1px solid #2d3748; color: #64748b; padding: 6px 12px; border-radius: 8px; cursor: pointer; font-size: 11px; }
    .table-filters button.active { background: #38bdf8; color: #000; border-color: #38bdf8; }

    /* MOBILE TRANSFORMATION (2 COLUMNS) */
    @media (max-width: 1024px) {
        .main-content { grid-template-columns: 1fr; }
        .sidebar-glass { position: relative; top: 0; width: 100%; box-sizing: border-box; }
    }

    @media (max-width: 768px) {
        .table-zone-glass { padding: 10px; background: transparent; border: none; }
        .neo-table { display: block; width: 100% !important; }
        .neo-table thead { display: none; }
        .neo-table tbody { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 12px; 
            width: 100%;
        }
        .neo-row { 
            display: flex !important; 
            flex-direction: column; 
            background: #111422; 
            border: 1px solid #1e293b !important; 
            border-radius: 15px; 
            padding: 12px; 
            height: fit-content;
        }
        .neo-row td { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            padding: 8px 0 !important; 
            border-bottom: 1px solid #1a1f2e !important; 
            width: 100% !important;
            font-size: 12px !important;
            text-align: right;
        }
        .neo-row td:last-child { border-bottom: none !important; margin-top: 10px; justify-content: center; }
        .neo-row td::before { 
            content: attr(data-label); 
            font-weight: bold; 
            color: #64748b; 
            text-transform: uppercase; 
            font-size: 10px; 
            text-align: left; 
            flex: 1;
        }
        .row-actions { width: 100%; justify-content: space-around; }
        .edit-mini-btn, .delete-mini-btn { flex: 1; display: flex; justify-content: center; padding: 10px; }
    }

    @media (max-width: 480px) {
        .neo-table tbody { grid-template-columns: 1fr; }
        .stats-row-4 { grid-template-columns: 1fr 1fr; }
    }
`;

export default AdminWallet;